<script>
import { Modal } from 'bootstrap';
import { Html5Qrcode } from 'html5-qrcode';
import { getPublicCard } from '@/utils/business-card';

export default {
  emits: ['scanned'],
  data() {
    return { scanner: null, running: false, busy: false, error: '', pending: null };
  },

  mounted() {
    const el = this.$refs.modalEl;
    el.addEventListener('shown.bs.modal', this.start);
    el.addEventListener('hidden.bs.modal', this.onHidden);
  },

  beforeUnmount() {
    const el = this.$refs.modalEl;
    el.removeEventListener('shown.bs.modal', this.start);
    el.removeEventListener('hidden.bs.modal', this.onHidden);
    this.stop();
  },

  methods: {
    async start() {
      this.error = '';
      this.busy = false;
      try {
        this.scanner = new Html5Qrcode('qr-reader');
        await this.scanner.start(
          { facingMode: 'environment' },
          { fps: 10, qrbox: 240 },
          this.onDecode,
          () => {}
        );
        this.running = true;
      } catch (e) {
        this.error = 'Could not access the camera. Allow camera permission, or upload a QR image instead.';
      }
    },

    async stop() {
      try { if (this.scanner && this.running) await this.scanner.stop(); } catch (e) { /* ignore */ }
      try { this.scanner?.clear(); } catch (e) { /* ignore */ }
      this.running = false;
      this.scanner = null;
    },

    async onHidden() {
      await this.stop();
      if (this.pending) {
        const data = this.pending;
        this.pending = null;
        this.$emit('scanned', data);
      }
    },

    async onDecode(text) {
      if (this.busy) return;
      this.busy = true;
      try {
        this.pending = await this.parse(text);
        Modal.getInstance(this.$refs.modalEl)?.hide();
      } catch (e) {
        this.error = e.message || 'Could not read this QR code.';
        this.busy = false;
      }
    },

    // Scan from an uploaded image (fallback when camera is unavailable)
    async onFile(event) {
      const file = event.target.files?.[0];
      event.target.value = '';
      if (!file) return;
      this.error = '';
      try {
        await this.stop();
        const temp = new Html5Qrcode('qr-reader-file');
        const text = await temp.scanFile(file, false);
        this.busy = true;
        this.pending = await this.parse(text);
        Modal.getInstance(this.$refs.modalEl)?.hide();
      } catch (e) {
        this.busy = false;
        this.error = e.message || 'No QR code found in this image.';
      }
    },

    async parse(text) {
      const raw = (text || '').trim();

      if (/^BEGIN:VCARD/i.test(raw)) return this.parseVcard(raw);

      const m = raw.match(/\/card\/([A-Za-z0-9]+)/);
      if (m) {
        const res = await getPublicCard(m[1]);
        const d = res.data.data || {};
        return this.normalize({
          name: d.name, mobile: d.phone || d.whatsapp, email: d.email,
          company: d.company, designation: d.designation, website: d.website,
        });
      }
      throw new Error('This is not a business card QR code.');
    },

    parseVcard(text) {
      const out = {};
      text.split(/\r?\n/).forEach(line => {
        const i = line.indexOf(':');
        if (i < 0) return;
        const key = line.slice(0, i).split(';')[0].toUpperCase();
        const val = line.slice(i + 1).trim();
        if (key === 'FN') out.name = val;
        else if (key === 'TEL' && !out.mobile) out.mobile = val;
        else if (key === 'EMAIL' && !out.email) out.email = val;
        else if (key === 'ORG') out.company = val.replace(/;/g, ' ').trim();
        else if (key === 'TITLE') out.designation = val;
        else if (key === 'URL') out.website = val;
      });
      return this.normalize(out);
    },

    normalize(d) {
      let mobile = (d.mobile || '').replace(/\D/g, '');
      if (mobile.length === 12 && mobile.startsWith('91')) mobile = mobile.slice(2);
      return {
        name: d.name || '',
        mobile,
        email: d.email || '',
        company: d.company || '',
        designation: d.designation || '',
        website: d.website || '',
      };
    },
  },
};
</script>

<template>
  <div class="modal fade" id="scan_card_modal" tabindex="-1" ref="modalEl">
    <div class="modal-dialog modal-dialog-centered">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">Scan Business Card</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal"></button>
        </div>

        <div class="modal-body text-center">
          <div id="qr-reader" style="width:100%; min-height:260px;"></div>
          <div id="qr-reader-file" style="display:none;"></div>

          <div v-if="error" class="alert alert-warning py-2 mt-3 mb-0" style="font-size:12.5px;">{{ error }}</div>
          <div v-else-if="busy" class="mt-3" style="font-size:12.5px;">Reading card...</div>
          <div v-else class="mt-3" style="font-size:12.5px; color:#64748b;">Point the camera at the QR code on the card.</div>
        </div>

        <div class="modal-footer justify-content-center">
          <label class="btn btn-light mb-0">
            <i class="ti ti-photo-up me-1"></i>Upload QR image
            <input type="file" accept="image/*" hidden @change="onFile">
          </label>
        </div>
      </div>
    </div>
  </div>
</template>